import * as eligibilityActions from '../actions/eligibility.actions';
import * as requestsActions from '../actions/requests.actions';
import createMiddleware from './helper';

const isEligibilitySatisfied = status => status === 'success' || status === 'not_applicable';

function hasAnySatisfiedEligibilityWithNoChanges(coverages, changes) {
  return coverages.some(c => isEligibilitySatisfied(c.status)) && changes.length === 0;
}

export const checkEligibilityMdl = createMiddleware(eligibilityActions.checkEligibility, ({ dispatch, payload }) => {
  const { coordinationTypeId, isAgentMode } = payload;
  dispatch(eligibilityActions.setLoading({ coordinationTypeId, value: true, isAgentMode }));
});

export const checkEligibilitySuccessMdl = createMiddleware(
  eligibilityActions.checkEligibilitySuccess,
  ({ dispatch, payload, getState }) => {
    const {
      coordinationTypeId,
      sessionId,
      error,
      reportContent,
      status,
      appliedRules,
      isAgentMode,
      verificationId,
      agent,
    } = payload;
    const { infobox } = getState();
    const coverage = infobox.coverages[coordinationTypeId];

    dispatch(eligibilityActions.loadPortalInfo(coverage.payer.payerId));

    if (error) {
      dispatch(eligibilityActions.resetButtons());
      dispatch(eligibilityActions.error(error));
      return;
    }

    if (reportContent || status) {
      dispatch(eligibilityActions.resetButtons());
      if (reportContent) {
        dispatch(eligibilityActions.setHTML(reportContent));
      }

      if (isAgentMode) {
        const changes = agent?.changes || [];
        const issues = agent?.issues || [];

        if (isEligibilitySatisfied(status) && changes.length === 0) {
          const reqId = getState().requests.request?.id;
          if (reqId) {
            dispatch(requestsActions.editRequest(reqId));
          }
        }

        dispatch(
          eligibilityActions.showChangesModal({
            status: status || 'unknown',
            appliedRules: appliedRules || [],
            changes,
            issues,
            coordinationTypeId,
            verificationId,
            pendingReview: !!payload?.pendingReview,
          }),
        );
      }

      return;
    }

    dispatch(eligibilityActions.setCreating({ coordinationTypeId, value: true, sessionId }));
  },
);

function checkEligibilitySessionId(state, sessionId) {
  return Object.values(state.eligibility.eligibilities).find(eligibility => eligibility.sessionId === sessionId);
}

export const socketResponseReceivedMdl = createMiddleware(
  eligibilityActions.socketResponseReceived,
  ({ dispatch, action, getState }) => {
    const { meta, resource_id } = action.payload;

    if (!checkEligibilitySessionId(getState(), resource_id)) {
      return;
    }

    if (meta.error) {
      dispatch(eligibilityActions.resetButtons());
      dispatch(eligibilityActions.error(meta.error));
      return;
    }

    dispatch(eligibilityActions.checkEligibility({ coordinationTypeId: meta.coordination_type_id }));
  },
);

export const portalInfoLoadedMdl = createMiddleware(eligibilityActions.portalInfoLoaded, ({ payload, dispatch }) => {
  const [payerOptions] = payload;

  if (payerOptions) {
    dispatch(eligibilityActions.setPortalInfo(payerOptions.portalInfo || ''));
  }
});

export const closeFrameMdl = createMiddleware(eligibilityActions.closeFrame, ({ dispatch }) => {
  dispatch(eligibilityActions.resetButtons());
});

export const checkMultiEligibilityMdl = createMiddleware(eligibilityActions.checkMultiEligibility, ({ dispatch }) => {
  dispatch(requestsActions.clearRequestRefreshDialog());
  dispatch(eligibilityActions.setAgentProcessing(true));
});

export const checkMultiEligibilitySuccessMdl = createMiddleware(
  eligibilityActions.checkMultiEligibilitySuccess,
  ({ dispatch, payload, getState }) => {
    dispatch(eligibilityActions.setAgentProcessing(false));
    dispatch(eligibilityActions.resetAuditEntries());

    if (payload.error) {
      dispatch(eligibilityActions.error(payload.error));
      return;
    }

    const changes = payload.changes || [];
    const issues = payload.issues || [];
    const coverages = payload.coverages || [];
    const summary = payload.summary || '';
    const verificationId = payload.verificationId || payload.id;
    const changesStatus = payload.changesStatus || null;
    const isBillerVerified = payload.isBillerVerified || false;
    const verifiedAt = payload.createdAt || null;

    const needsBillerVerificationRefresh = isBillerVerified && !getState().requests.isBillerVerified;
    const needsEligibilityRefresh =
      !isBillerVerified &&
      (hasAnySatisfiedEligibilityWithNoChanges(coverages, changes) || changesStatus === 'accepted');

    if (needsBillerVerificationRefresh || needsEligibilityRefresh) {
      const reqId = getState().requests.request?.id;
      if (reqId) {
        dispatch(requestsActions.editRequest(reqId));
      }
    }

    dispatch(
      eligibilityActions.showEligibilityView({
        verificationId,
        changes,
        issues,
        coverages,
        summary,
        changesStatus,
        isBillerVerified,
        verifiedAt,
        pendingReview: !!payload.pendingReview,
      }),
    );
  },
);

export const hideEligibilityViewMdl = createMiddleware(eligibilityActions.hideEligibilityView, ({ dispatch }) => {
  dispatch(eligibilityActions.setAgentProcessing(false));
});

export const eligibilityMdl = [
  checkEligibilityMdl,
  checkEligibilitySuccessMdl,
  socketResponseReceivedMdl,
  portalInfoLoadedMdl,
  closeFrameMdl,
  checkMultiEligibilityMdl,
  checkMultiEligibilitySuccessMdl,
  hideEligibilityViewMdl,
];
